import { useState } from 'react'
import Input from '../../../components/Input/Input'
import Button from '../../../components/Button/Button'
import Calendar from '../../../components/Calendar/Calendar'
import { CrossCloseIcon } from '../../../components/Icons/Icons'
import Dropdown from '../../../components/Dropdown/Dropdown'
import usePinnedFeatureFlag from '../../../hooks/usePinnedFeatureFlag'
import { DEBT_AMOUNT_DECIMALS } from '../../../config/featureFlagKeys'
import { sanitizeMoneyInput } from '../../../utils/amount'

const SelfEnrollmentDebtFields = ({
  fields,
  setFields,
  initialFieldSet,
  setErrors,
  errors,
  creditReportTypes = [],
  creditReportStatuses = [],
  isUpdate = false,
}) => {
  const [selectedTypes, setSelectedTypes] = useState({})
  const [selectedStatuses, setSelectedStatuses] = useState({})
  const { enabled: amountDecimals } = usePinnedFeatureFlag(DEBT_AMOUNT_DECIMALS)
  const amountValue = (value) => (amountDecimals ? sanitizeMoneyInput(value) : value?.replace(/[.,]/g, ''))

  const handleAddFields = () => {
    setFields([...fields, { ...initialFieldSet }])
  }

  const handleRemoveFields = (index) => {
    const updatedFields = fields?.filter((_, i) => i !== index)
    setFields(updatedFields)

    const newSelectedTypes = { ...selectedTypes }
    delete newSelectedTypes[index]
    setSelectedTypes(newSelectedTypes)

    const newSelectedStatuses = { ...selectedStatuses }
    delete newSelectedStatuses[index]
    setSelectedStatuses(newSelectedStatuses)
  }

  const handleFieldChange = (index, field, value) => {
    const updatedFields = [...fields]
    updatedFields[index][field] = value
    setFields(updatedFields)

    if (value && Array.isArray(errors) && errors.length > 0) {
      const updatedErrors = [...errors]
      updatedErrors[index] = { ...updatedErrors[index], [field]: false }
      setErrors(updatedErrors)
    }
  }

  const handleAccountNumberBlur = (index, value) => {
    const length = value.length

    setErrors((prev) => {
      const updated = [...prev]
      if (length < 8 || length > 17) {
        updated[index] = {
          ...updated[index],
          accountNumber: "Account number should be 8 to 17 characters long",
        }
      } else {
        updated[index] = {
          ...updated[index],
          accountNumber: null,
        }
      }
      return updated
    })
  }

  const formattedTypes = creditReportTypes?.map(item => ({
    value: item._id,
    label: item.description
  })) || []

  const formattedStatuses = creditReportStatuses?.map(item => ({
    value: item._id,
    label: item.description
  })) || []

  return (
    <div className="w-full flex flex-col">
      {fields?.map((field, index) => (
        <div key={index} className="flex flex-col mb-4 md:mb-8">
          <div data-testid="self-enrollment-debt-row" className="flex border border-divider rounded-lg p-3 md:p-5 shadow-card dark:shadow-none justify-start items-start">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              <Dropdown
                height="48px"
                heightPaddings={{ paddingBottom: "2px" }}
                label="Account Type"
                value={selectedTypes[index] || field.accountType}
                className="w-full"
                menuItems={formattedTypes}
                setValue={(e) => {
                  setSelectedTypes(prev => ({ ...prev, [index]: e }))
                  handleFieldChange(index, "accountType", e)
                }}
                placeholder="Select Account Type"
              />
              <Input
                type="text"
                classes="h-12"
                width="w-full"
                minLength={8}
                maxLength={17}
                label="Account Number"
                placeholder="Enter account number"
                value={field?.accountNumber}
                onChange={(e) =>
                  handleFieldChange(index, "accountNumber", e.target.value.replace(/[^a-zA-Z0-9]/g, ''))
                }
                onBlur={(e) => handleAccountNumberBlur(index, e.target.value)}
              />
              <Calendar
                height="42px"
                calendarWidth="100%"
                label="Opened Date"
                placeholder="MM-DD-YYYY"
                value={field.openedDate}
                onChange={(date) => handleFieldChange(index, "openedDate", date)}
              />
              <Calendar
                height="42px"
                calendarWidth="100%"
                label="Reported Date"
                placeholder="MM-DD-YYYY"
                value={field.reportedDate}
                onChange={(date) => handleFieldChange(index, "reportedDate", date)}
              />
              <Calendar
                height="42px"
                calendarWidth="100%"
                label="Closed Date"
                placeholder="MM-DD-YYYY"
                value={field.closedDate}
                onChange={(date) => handleFieldChange(index, "closedDate", date)}
              />
              <Calendar
                height="42px"
                label="Last Payment Date"
                calendarWidth="100%"
                placeholder="MM-DD-YYYY"
                value={field.lastPaymentDate}
                onChange={(date) => handleFieldChange(index, "lastPaymentDate", date)}
              />
              <Dropdown
                height="48px"
                heightPaddings={{ paddingBottom: "2px" }}
                label="Account Open or Closed"
                value={field.isOpenAccount}
                className="w-full"
                menuItems={[{ label: "Open", value: true }, { label: "Closed", value: false }]}
                setValue={(value) => handleFieldChange(index, "isOpenAccount", value)}
                placeholder="Select Status"
              />
              <Dropdown
                height="48px"
                heightPaddings={{ paddingBottom: "2px" }}
                label="Account Status"
                value={selectedStatuses[index] || field.accountStatus}
                className="w-full"
                menuItems={formattedStatuses}
                setValue={(e) => {
                  setSelectedStatuses(prev => ({ ...prev, [index]: e }))
                  handleFieldChange(index, "accountStatus", e)
                }}
                placeholder="Select Status"
              />
              <Input
                classes="h-12"
                width="w-full"
                label="Creditor Name"
                placeholder="Bank Of America"
                value={field.creditorName}
                onChange={(e) => handleFieldChange(index, "creditorName", e.target.value)}
              />
              <Input
                type="number"
                step={amountDecimals ? 'any' : undefined}
                classes="h-12"
                width="w-full"
                label="Balance Owed"
                placeholder="0.00"
                value={field.balanceAmount}
                onChange={(e) => handleFieldChange(index, "balanceAmount", amountValue(e.target.value))}
              />
              <Input
                type="number"
                step={amountDecimals ? 'any' : undefined}
                classes="h-12"
                width="w-full"
                label="Past Due Amount"
                placeholder="0.00"
                value={field.pastDueAmount}
                onChange={(e) => handleFieldChange(index, "pastDueAmount", amountValue(e.target.value))}
              />
              <Input
                type="number"
                step={amountDecimals ? 'any' : undefined}
                classes="h-12"
                width="w-full"
                label="Credit Limit"
                placeholder="0.00"
                value={field.creditLimit}
                onChange={(e) => handleFieldChange(index, "creditLimit", amountValue(e.target.value))}
              />
            </div>

            {!isUpdate && (
              <button className="ml-2 flex-shrink-0" onClick={() => handleRemoveFields(index)}>
                <CrossCloseIcon fill="var(--color-gray-600)" />
              </button>
            )}
          </div>
        </div>
      ))}
      {!isUpdate && (
        <Button onClick={handleAddFields} type="text" styles={{ marginRight: '-14px' }}>+Add New Debt</Button>
      )}
    </div>
  )
}

export default SelfEnrollmentDebtFields
